import { BookOpenText } from 'lucide-react'
import { Crest, CrestWatermark } from '@/components/Logo'
import { Button } from '@/components/ui'
import { daysUntil, type Booklet } from '@shared/model'
import { formatDate, formatDays, formatSek } from '@/lib/format'

/**
 * Häftets omslag.
 *
 * Ersätter sparpanelen som tidigare låg överst. Panelen med nivåer, mätare
 * och progress har flyttat till Mitt konto – erbjudandena ska äga
 * förstasidan, och belöningen får inte stå i vägen för varan.
 *
 * Men sparbeloppet är hela drivkraften i appen, och något osynligt slutar
 * motivera. Det står därför kvar här som en tyst rad: en siffra, ingen
 * mätare, inga nivåer. Vill man se hela bilden finns den ett tryck bort.
 *
 * Ytan har en lutning och inte en plan färg. En plan mättad yta över halva
 * skärmen läser som affisch, en lutning läser som material.
 */
export function BookletHeader({
  booklet,
  totalSavedSek,
  offerCount,
  bookletValueSek,
  onOpenFlipbook,
}: {
  booklet: Booklet
  totalSavedSek: number
  offerCount: number
  /** Summan av alla erbjudandens schablonvärden. */
  bookletValueSek: number
  onOpenFlipbook: () => void
}) {
  const daysLeft = daysUntil(booklet.endDate)
  const expired = booklet.status === 'expired'
  const upcoming = booklet.status === 'upcoming'

  const period = upcoming
    ? `Öppnar ${formatDate(booklet.startDate)}`
    : expired
      ? `Gick ut ${formatDate(booklet.endDate)}`
      : daysLeft === 0
        ? 'Sista dagen idag'
        : `${formatDays(daysLeft)} kvar`

  return (
    <header className="-mx-4 -mt-4 overflow-hidden bg-brand-surface px-4 pt-6 pb-5 text-on-brand">
      <div className="relative flex flex-col items-center gap-2.5 text-center">
        {/* Klubbmärket som vattenstämpel, nedtonat till en antydan. Samma
            grepp som på inloggningen, men bara ett märke: omslaget är lågt
            och har redan sigillet i mitten. */}
        <CrestWatermark width={90} className="-top-2 right-2 rotate-[14deg] opacity-[0.11]" />

        {/* Märket i vit cirkel, som på ett omslag. Klubbmärket har gul kontur
            hela vägen runt och skulle synas även direkt mot blått – men
            cirkeln ger det luft och läser som ett sigill. */}
        <span className="relative flex size-16 items-center justify-center rounded-pill bg-white shadow-card">
          <Crest size={44} />
        </span>

        <div className="relative">
          <h1 className="font-display text-2xl leading-none font-extrabold tracking-tight">
            Lirahäftet
          </h1>
          <p className="mt-1 text-sm text-on-brand/75">
            {booklet.periodName} · {period}
          </p>
          {/*
           * En rad som byter innebörd allteftersom häftet används.
           *
           * Innan man löst in något är häftets värde ett säljargument: det
           * säger vad man håller i. Efteråt är samma summa nämnaren – "1 250
           * av 5 500" säger både hur mycket man tjänat och hur mycket som
           * står kvar outnyttjat. Två separata rader hade sagt samma sak
           * sämre, och tre rader under en rubrik blir en lista i stället för
           * ett omslag.
           *
           * Hela bilden med nivåer och mätare bor på Mitt konto.
           */}
          <p className="mt-1.5 text-sm font-semibold text-accent">
            {bookletValueSek > 0
              ? totalSavedSek > 0
                ? `Du har sparat ${formatSek(totalSavedSek)} av ${formatSek(bookletValueSek)}`
                : `${offerCount} erbjudanden värda ${formatSek(bookletValueSek)}`
              : totalSavedSek > 0
                ? `Du har sparat ${formatSek(totalSavedSek)}`
                : `${offerCount} erbjudanden väntar på dig`}
          </p>
        </div>

        <Button
          variant="accent"
          size="md"
          className="relative mt-0.5"
          icon={<BookOpenText size={18} />}
          disabled={offerCount === 0}
          onClick={onOpenFlipbook}
        >
          Bläddra i häftet
        </Button>
      </div>
    </header>
  )
}
